/**
 * Elevation profile sampler — sample terrain elevation along a line.
 *
 * Given start/end coordinates and a Giro3D Map (with an ElevationLayer),
 * this module interpolates N points along the line and queries the
 * Map's elevation at each point. The result is an array of samples
 * suitable for rendering a Chart.js line chart.
 *
 * Uses `map.getElevation({ coordinates })` from Giro3D when available,
 * with a fallback to raycasting against the scene's mesh.
 */

import type { ElevationProfileSample } from '@/sdk-stub/gtwin-sdk/terrain-types';

export interface SampleLineOptions {
  /** Number of sample points along the line (default 64). */
  sampleCount?: number;
}

/**
 * Compute the haversine distance between two [lon, lat] points (metres).
 */
function haversineDistance(
  a: [number, number],
  b: [number, number],
): number {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const ha =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(ha));
}

/**
 * Linearly interpolate between two [lon, lat] points.
 */
function lerpCoord(
  a: [number, number],
  b: [number, number],
  t: number,
): [number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Sample elevation along a line from start to end coordinates.
 *
 * @param map — Giro3D Map entity (must have an ElevationLayer)
 * @param start — [lon, lat] start point
 * @param end — [lon, lat] end point
 * @param options — sampling options
 * @returns Array of elevation profile samples
 */
export async function sampleElevationProfile(
  map: unknown,
  start: [number, number],
  end: [number, number],
  options: SampleLineOptions = {},
): Promise<ElevationProfileSample[]> {
  const count = options.sampleCount ?? 64;
  const totalDist = haversineDistance(start, end);

  const samples: ElevationProfileSample[] = [];
  const m = map as any;

  for (let i = 0; i <= count; i++) {
    const t = i / count;
    const [lon, lat] = lerpCoord(start, end, t);
    const distance = totalDist * t;

    let elevation = 0;
    try {
      if (typeof m?.getElevation === 'function') {
        const result = m.getElevation({ coordinates: { x: lon, y: lat } });
        elevation =
          typeof result === 'number'
            ? result
            : result?.z ?? result?.elevation ?? 0;
      }
    } catch {
      elevation = 0;
    }

    samples.push({ distance, elevation, lon, lat });
  }

  return samples;
}

/**
 * Compute basic statistics for a profile.
 */
export function profileStats(samples: ElevationProfileSample[]): {
  minElevation: number;
  maxElevation: number;
  totalDistance: number;
  totalAscent: number;
  totalDescent: number;
} {
  if (samples.length === 0) {
    return { minElevation: 0, maxElevation: 0, totalDistance: 0, totalAscent: 0, totalDescent: 0 };
  }

  let min = Infinity;
  let max = -Infinity;
  let ascent = 0;
  let descent = 0;

  for (let i = 0; i < samples.length; i++) {
    const e = samples[i].elevation;
    if (e < min) min = e;
    if (e > max) max = e;
    if (i > 0) {
      const diff = e - samples[i - 1].elevation;
      if (diff > 0) ascent += diff;
      else descent += Math.abs(diff);
    }
  }

  return {
    minElevation: min,
    maxElevation: max,
    totalDistance: samples[samples.length - 1]?.distance ?? 0,
    totalAscent: ascent,
    totalDescent: descent,
  };
}
